import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { accountAttentionLabel } from '@/features/accounts/attention-labels';
import {
  formatAccountLabel,
  visibleAccounts,
  type Account,
} from '@/features/accounts/accounts.api';
import { useAccounts } from '@/features/accounts/useAccounts';

/**
 * The adapter's hub↔gateway link-loss stamp: on every WS drop (each deploy,
 * gateway restart and hub shutdown) the hub stamps every known number
 * `down` + this detail. Contract B literal — `GATEWAY_LOST_DETAIL` in
 * `hub/app/adapter/types.py`, `docs/contracts/openclaw-adapter.md`.
 */
const GATEWAY_LOST_DETAIL = 'gateway connection lost';

/** What this hook remembers about one account between snapshots. */
interface ToastMemory {
  /** The last ACCOUNT-scope verdict was critical — an error toast is owed
   *  only on entering critical from anything else. */
  critical: boolean;
  /** Critical at some point since the number was last `connected` — its
   *  「已恢复」 is owed once it is `connected` again. */
  episode: boolean;
}

const HEALTHY: ToastMemory = { critical: false, episode: false };

/**
 * A snapshot that says nothing about the number itself: FLEET-scope attention
 * (`gateway_link_lost`, `restart_breaker` — the fleet banner's business), or a
 * row still carrying the link-loss stamp. Once the link is back and the fleet
 * is being re-scored, the hub reads a not-yet-rewritten stamp as a plain
 * account-scope `down`; it is still "state unknown", not a verdict. Mirrors
 * `hub/app/account_health.py` `plan_alerts`, which never lets either touch —
 * or baseline — its per-account memory.
 */
function describesOnlyTheLink(account: Account): boolean {
  const attention = account.attention;
  if (attention != null && attention.scope !== 'account') return true;
  return account.status === 'down' && account.status_detail === GATEWAY_LOST_DETAIL;
}

/**
 * Fires a toast the moment an account ENTERS account-scope critical
 * attention, and a recovery toast once it is back — spec 01 §3.5: "Newly
 * critical account-scope attention → one toast.error per episode (`id:
 * attention-{id}`); recovery → toast.success." Mounted once in `Layout.tsx`
 * (the app shell) so it observes every account regardless of which
 * conversation is open.
 *
 * Scope is deliberately narrower than `FleetStatusBanner`'s priority list:
 * FLEET-scope attention (`gateway_link_lost`, `restart_breaker`) is a single
 * shared condition already surfaced loudly by the fleet banner — toasting it
 * per account would fire one toast per row for the SAME outage. Only
 * `scope: 'account'` verdicts toast here (`banned` / `session_lost` /
 * `connecting_stuck` — the three critical, per-account reasons).
 *
 * Episodes follow the hub's own alert planner (`account_health.plan_alerts`,
 * runbook "How the hub decides"), so a hub or gateway restart is never
 * reported as a recovery — and then re-reported as the same outage:
 *
 *   - Fleet-scope attention and the link-loss stamp are "state unknown": they
 *     neither toast nor change what is remembered about the number
 *     ({@link describesOnlyTheLink}).
 *   - 「已恢复」 only once the number is `connected` again. The `connecting`
 *     hop that follows every restart, reconnect or re-scan (no attention for
 *     its first 5 minutes) keeps the episode open, and so does a warning (a
 *     re-scanned number awaiting its QR, a stopped number) — none of those
 *     can send.
 *   - Entering critical from anything else (healthy, a warning) is a new
 *     error toast; switching between two criticals is not.
 *
 * "One toast per episode": a continuous critical period toasts exactly once
 * on entry, not on every 30s poll / WS patch while it stays critical —
 * tracked in a ref (mutated, no re-render) rather than React state. An
 * account's FIRST real verdict is a BASELINE — mirrors the hub's "first
 * sighting = baseline, no alert" rule — so an already-critical account
 * doesn't toast-bomb the agent on every page load (a critical baseline still
 * owes its 「已恢复」). `toast.error`/`toast.success` also carry a stable
 * `id: attention-{id}`, so even a duplicate call (e.g. a React Strict Mode
 * double-invoke) replaces rather than stacks.
 */
export function useAttentionToasts(): void {
  const accountsQuery = useAccounts();
  const accounts = accountsQuery.data;
  // Per-account memory; an account absent here has not had a real verdict yet.
  const memoryRef = useRef<Map<string, ToastMemory> | null>(null);

  useEffect(() => {
    if (!accounts) return;

    const previous = memoryRef.current ?? new Map<string, ToastMemory>();
    // Rebuilt from the current list, so a deleted account is forgotten.
    const next = new Map<string, ToastMemory>();

    // Hidden (archived) accounts are out of the pool: never toasted, and
    // forgotten like a deleted account (re-baselined if ever restored).
    for (const account of visibleAccounts(accounts)) {
      const before = previous.get(account.id);

      if (describesOnlyTheLink(account)) {
        if (before) next.set(account.id, before); // carried untouched
        continue;
      }

      const attention = account.attention ?? null;
      const isCritical = attention !== null && attention.level === 'critical';

      if (before === undefined) {
        // First real verdict for this account: baseline — record, never toast.
        next.set(account.id, { critical: isCritical, episode: isCritical });
        continue;
      }

      const label = formatAccountLabel(account);
      if (attention !== null && isCritical) {
        if (!before.critical) {
          // accountAttentionLabel: a storm-held ban says 未停用 (not stopped).
          const reason = accountAttentionLabel(account, attention, Date.now());
          toast.error(`${label} ${reason}`, { id: `attention-${account.id}` });
        }
        next.set(account.id, { critical: true, episode: true });
      } else if (attention !== null) {
        // An account-scope warning: de-escalated, but not back — the episode
        // (if any) stays open.
        next.set(account.id, { critical: false, episode: before.episode });
      } else if (account.status === 'connected') {
        if (before.episode) {
          toast.success(`${label} 已恢复`, { id: `attention-${account.id}` });
        }
        next.set(account.id, HEALTHY);
      } else {
        // No verdict yet (`connecting`, not stuck): the reconnect hop — hold.
        next.set(account.id, before);
      }
    }

    memoryRef.current = next;
  }, [accounts]);
}
